/**
 * Craps — BETohalics house table (Phase 1).
 * Wallet-native, server-authoritative dice + resolution.
 * See /app/backend/routes/craps.py for full rules.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft, HelpCircle, Coins, Sparkles, RotateCcw, History,
    Info, Volume2, VolumeX, Dice5, Undo2, Trash2, Music, Music2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useCrapsAudio } from '../hooks/useCrapsAudio';
import ChipStack from '../components/blackjack/ChipStack';
import HouseCrest from '../components/HouseCrest';
import WinFlash from '../components/WinFlash';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CHIP_DENOMS = [1, 5, 10, 25, 50, 100];

const PIP_LAYOUTS = {
    1: [[1,1]],
    2: [[0,0],[2,2]],
    3: [[0,0],[1,1],[2,2]],
    4: [[0,0],[2,0],[0,2],[2,2]],
    5: [[0,0],[2,0],[1,1],[0,2],[2,2]],
    6: [[0,0],[2,0],[0,1],[2,1],[0,2],[2,2]],
};

/**
 * TableChip — a realistic casino-style poker chip rendered on the felt to
 * represent an active wager. Colored by denomination (standard US casino
 * conventions), with a white dashed edge pattern, striped rim, and the amount
 * printed in the center. Multiple physical chips are shown as a small stack.
 */
const CHIP_STYLE = (amt) => {
    // Pick the color that "represents" this stack (largest fitting denom)
    if (amt >= 500) return { bg: '#4b0082', edge: '#f5f5f5', text: '#ffffff', label: 'PURPLE' };
    if (amt >= 100) return { bg: '#111827', edge: '#f5f5f5', text: '#ffffff', label: 'BLACK' };
    if (amt >= 50)  return { bg: '#ea580c', edge: '#f5f5f5', text: '#ffffff', label: 'ORANGE' };
    if (amt >= 25)  return { bg: '#15803d', edge: '#f5f5f5', text: '#ffffff', label: 'GREEN' };
    if (amt >= 10)  return { bg: '#1d4ed8', edge: '#f5f5f5', text: '#ffffff', label: 'BLUE' };
    if (amt >= 5)   return { bg: '#b91c1c', edge: '#f5f5f5', text: '#ffffff', label: 'RED' };
    return           { bg: '#f8fafc', edge: '#0b1220', text: '#0b1220', label: 'WHITE' };
};

const TableChip = ({ amount, small }) => {
    if (!amount) return null;
    const { bg, edge, text } = CHIP_STYLE(amount);
    const size = small ? 28 : 36;
    // Decompose the stack for visual depth — top chip shows the total
    const depth = amount >= 100 ? 3 : amount >= 25 ? 2 : 1;
    return (
        <div className="relative pointer-events-none" style={{ width: size, height: size + (depth - 1) * 3 }}>
            {[...Array(depth)].map((_, i) => (
                <div
                    key={i}
                    className="absolute rounded-full"
                    style={{
                        left: 0,
                        bottom: (depth - 1 - i) * 3,      // top chip = last drawn
                        width: size,
                        height: size,
                        background: `radial-gradient(circle at 50% 40%, ${bg} 45%, ${bg}dd 100%)`,
                        boxShadow: `0 2px 3px rgba(0,0,0,0.55), inset 0 0 0 3px ${bg}, inset 0 0 0 5px ${edge}, inset 0 0 0 6px ${bg}, inset 0 0 0 10px ${edge}55, inset 0 -3px 4px rgba(0,0,0,0.4)`,
                    }}
                >
                    {/* Dashed edge — 8 evenly-spaced marks around the rim */}
                    <div className="absolute inset-0 rounded-full overflow-hidden">
                        {[...Array(8)].map((__, j) => (
                            <div key={j}
                                className="absolute left-1/2 top-0 origin-bottom"
                                style={{
                                    width: 3, height: 6, background: edge,
                                    transform: `translate(-50%, 0) rotate(${j * 45}deg)`,
                                    transformOrigin: `50% ${size / 2}px`,
                                    opacity: 0.9,
                                }}
                            />
                        ))}
                    </div>
                    {/* Amount label — only on top chip */}
                    {i === depth - 1 && (
                        <div className="absolute inset-0 flex items-center justify-center font-black font-mono"
                             style={{ color: text, fontSize: small ? 9 : 11, textShadow: `0 1px 0 rgba(0,0,0,0.35)` }}>
                            ${amount % 1 === 0 ? amount : amount.toFixed(2)}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

/**
 * MiniDie / MiniDicePair — tiny inline dice visuals used on prop-bet buttons so
 * beginners can see at a glance which dice combo wins the bet. Uses the same
 * PIP_LAYOUTS as the main dice so it's visually consistent.
 */
const MiniDie = ({ value, size = 18 }) => (
    <div
        className="relative rounded-[3px] bg-white border border-black/40"
        style={{ width: size, height: size, boxShadow: '0 1px 2px rgba(0,0,0,0.35)' }}
    >
        <div className="absolute inset-[2px] grid grid-cols-3 grid-rows-3 gap-[1px]">
            {[...Array(9)].map((_, i) => {
                const col = i % 3;
                const row = Math.floor(i / 3);
                const pips = PIP_LAYOUTS[value] || [];
                const on = pips.some(([c, r]) => c === col && r === row);
                return <div key={i} className={`rounded-full ${on ? 'bg-black' : 'bg-transparent'}`} style={on ? { boxShadow: 'inset 0 0 0 0.5px rgba(0,0,0,0.8)' } : {}} />;
            })}
        </div>
    </div>
);

const MiniDicePair = ({ a, b, size = 18 }) => (
    <div className="flex items-center gap-0.5 justify-center">
        <MiniDie value={a} size={size} />
        <MiniDie value={b} size={size} />
    </div>
);

/**
 * Classic craps stickman slang for every dice combination. Called out over
 * the dice on every roll to give the table that real-casino energy.
 * Returns `{ name, sub }` — `sub` is a short flavor tagline shown smaller.
 */
const SLANG = (d1, d2, phase, point) => {
    const total = d1 + d2;
    const hard = d1 === d2;
    // Special calls that override the total-based ones
    if (total === 2)  return { name: 'SNAKE EYES', sub: 'craps · line loses' };
    if (total === 3 && ((d1 === 1 && d2 === 2) || (d1 === 2 && d2 === 1))) return { name: 'ACE DEUCE', sub: 'craps · line loses' };
    if (total === 12) return { name: 'BOXCARS', sub: 'midnight · craps' };
    if (total === 11) return { name: 'YO ELEVEN', sub: '5 and 6 — the yo!' };
    // Hard-way / easy-way callouts
    if (total === 4)  return hard ? { name: 'LITTLE JOE', sub: 'hard four!' }        : { name: 'EASY FOUR', sub: '' };
    if (total === 5)  return { name: 'FEVER FIVE', sub: 'no field five' };
    if (total === 6)  return hard ? { name: 'HARD SIX', sub: 'six the hard way' }    : { name: 'SIXIE FROM DIXIE', sub: '' };
    if (total === 7)  {
        if (phase === 'come_out') return { name: 'SEVEN WINNER', sub: 'natural — line pays' };
        return { name: 'SEVEN OUT', sub: 'line down · new shooter' };
    }
    if (total === 8)  return hard ? { name: 'HARD EIGHT', sub: 'eight the hard way' } : { name: 'EIGHTER FROM DECATUR', sub: '' };
    if (total === 9)  return { name: 'CENTER FIELD 9', sub: 'nina · nine' };
    if (total === 10) return hard ? { name: 'BIG BEN', sub: 'hard ten!' }              : { name: 'EASY TEN', sub: '' };
    return { name: `${total}`, sub: '' };
};

const Die = ({ value, rolling }) => (
    <motion.div
        className="relative w-16 h-16 md:w-20 md:h-20 rounded-xl bg-white shadow-lg border border-black/20"
        animate={rolling ? { rotate: 720, y: -18 } : { rotate: 0, y: 0 }}
        transition={rolling ? { duration: 0.85, ease: 'easeOut' } : { duration: 0 }}
        data-testid={`craps-die-${value ?? '?'}`}
    >
        {value != null && (
            <div className="absolute inset-2 grid grid-cols-3 grid-rows-3 gap-0.5">
                {[...Array(9)].map((_, i) => {
                    const col = i % 3;
                    const row = Math.floor(i / 3);
                    const pips = PIP_LAYOUTS[value] || [];
                    const on = pips.some(([c, r]) => c === col && r === row);
                    return <div key={i} className={`rounded-full ${on ? 'bg-black' : 'bg-transparent'}`} />;
                })}
            </div>
        )}
    </motion.div>
);

// A single betting spot on the felt. Clicking places `chip` on the current bet.
// `hint` — optional "chip $X wins $Y" preview shown as an italic tagline (helps
// entice newbies who don't know the payouts by heart).
const BetSpot = ({ label, sub, hint, testid, onClick, onTakeDown, chips, minWidth, active, glow, tone = 'gold', small, headline, diceHint }) => {
    const toneMap = {
        gold: 'border-[#FFD700]/40 hover:border-[#FFD700] text-[#FFD700]',
        red:  'border-[#FF3B30]/40 hover:border-[#FF3B30] text-[#FF3B30]',
        cyan: 'border-[#00E1FF]/40 hover:border-[#00E1FF] text-[#00E1FF]',
        green:'border-[#39FF14]/40 hover:border-[#39FF14] text-[#39FF14]',
        pink: 'border-[#FF007F]/40 hover:border-[#FF007F] text-[#FF007F]',
    };
    const glowMap = {
        gold: 'shadow-[0_0_22px_rgba(255,215,0,0.5)]',
        red:  'shadow-[0_0_22px_rgba(255,59,48,0.4)]',
        cyan: 'shadow-[0_0_22px_rgba(0,225,255,0.5)]',
        green:'shadow-[0_0_22px_rgba(57,255,20,0.5)]',
        pink: 'shadow-[0_0_22px_rgba(255,0,127,0.5)]',
    };
    const total = (chips || []).reduce((s, c) => s + c.amount, 0);
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
            className={`relative rounded-lg border-2 border-dashed bg-black/25 hover:bg-black/40 transition cursor-pointer select-none ${toneMap[tone]} ${active ? 'ring-2 ring-[#FFD700]/60 shadow-[0_0_18px_rgba(255,215,0,0.4)]' : ''} ${glow ? glowMap[tone] : ''} ${small ? 'px-1.5 py-1.5' : 'px-2 py-2'}`}
            style={minWidth ? { minWidth } : {}}
            data-testid={testid}
        >
            {headline && (
                <div className={`font-black font-serif tracking-wider leading-none ${small ? 'text-2xl' : 'text-4xl md:text-5xl'}`}
                     style={{ textShadow: `0 0 12px currentColor` }}>
                    {headline}
                </div>
            )}
            {diceHint && (
                <div className="mt-1 mb-0.5 flex justify-center" data-testid={`${testid}-dice-hint`}>
                    {diceHint}
                </div>
            )}
            <div className={`font-black tracking-wider ${headline || diceHint ? 'mt-0.5' : ''} ${small ? 'text-[10px]' : 'text-[12px] md:text-[13px]'}`}>{label}</div>
            {sub && <div className={`text-white/70 ${small ? 'text-[9px]' : 'text-[10px] md:text-[11px]'} font-mono mt-0.5`}>{sub}</div>}
            {hint && <div className={`text-[#FFEB88]/85 italic ${small ? 'text-[9px]' : 'text-[10px]'} font-mono mt-0.5`}>{hint}</div>}
            {total > 0 && (
                <div className={`absolute -bottom-2 -right-2 z-10`}>
                    <TableChip amount={total} small={small} />
                </div>
            )}
            {total > 0 && onTakeDown && (
                <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); onTakeDown(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onTakeDown(); } }}
                    className="absolute -top-1.5 -right-1.5 z-20 w-5 h-5 rounded-full bg-[#FF3B30] hover:bg-[#ff5b52] text-white text-[11px] font-black flex items-center justify-center border border-black/60 shadow-[0_2px_6px_rgba(0,0,0,0.5)] cursor-pointer"
                    title="Take down this bet"
                    aria-label="Take down this bet"
                    data-testid={`${testid}-takedown`}
                >
                    ×
                </div>
            )}
        </div>
    );
};

export default function Craps() {
    const nav = useNavigate();
    const { user, refreshUser } = useAuth();

    const [cfg, setCfg] = useState(null);
    const [state, setState] = useState(null);
    const [chip, setChip] = useState(5);
    const [busy, setBusy] = useState(false);
    const [rolling, setRolling] = useState(false);
    const [lastRoll, setLastRoll] = useState(null);
    const [resolved, setResolved] = useState([]);
    const [rulesOpen, setRulesOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [muted, setMuted] = useState(() => localStorage.getItem('craps-muted') === '1');
    const audio = useCrapsAudio();  // procedural SFX + ambient music
    const [celebration, setCelebration] = useState(null);   // { type: 'point-hit' | 'seven-out', total, payout }
    const [slangCall, setSlangCall] = useState(null);       // { name, sub, total }
    const [showPassHint, setShowPassHint] = useState(false); // finger-pointer reminder to bet Pass Line
    const [lastBets, setLastBets] = useState(null);          // snapshot of bets right before the last roll — used by "Rebet"
    const [winFlash, setWinFlash] = useState(null);          // current { amount, key, label }
    const [flashQueue, setFlashQueue] = useState([]);        // pending proposition flashes
    // Safety net — force-clears any lingering flash after MAX_FLASH_WINDOW_MS
    // so the overlay can NEVER get stuck on the screen (belt + suspenders on
    // top of WinFlash's own onDone). Reset on every new spin.
    const flashSafetyRef = useRef(null);
    const MAX_FLASH_WINDOW_MS = 7500;

    const clearFlashSafety = () => {
        if (flashSafetyRef.current) { clearTimeout(flashSafetyRef.current); flashSafetyRef.current = null; }
    };
    const armFlashSafety = () => {
        clearFlashSafety();
        flashSafetyRef.current = setTimeout(() => {
            setWinFlash(null);
            setFlashQueue([]);
            flashSafetyRef.current = null;
        }, MAX_FLASH_WINDOW_MS);
    };
    useEffect(() => () => clearFlashSafety(), []);  // cleanup on unmount

    // Fire the tier-appropriate audio for a given win amount (per-flash so
    // each proposition celebration gets its own soundtrack).
    const playFlashAudio = (amount) => {
        if (amount >= 500) audio.play('vault_kaching');
        else if (amount >= 100) audio.play('coin_cascade');
    };

    // Advance the flash queue when the current WinFlash finishes animating
    const handleFlashDone = () => {
        if (flashQueue.length > 0) {
            const [next, ...rest] = flashQueue;
            playFlashAudio(next.amount);
            setWinFlash(next);
            setFlashQueue(rest);
        } else {
            setWinFlash(null);
            clearFlashSafety();
        }
    };

    // Turn a resolved-bet snapshot into a punchy, casino-caller-style label.
    // Only "hero" propositions get individual pops; ordinary line/place wins
    // roll up into a single WINNER flash so we don't spam a 6-flash queue.
    const labelForBet = (r) => {
        const num = r.number;
        switch (r.kind) {
            case 'yo':          return 'YO ELEVEN!';
            case 'any_7':       return 'ANY SEVEN!';
            case 'any_craps':   return 'ANY CRAPS!';
            case 'c_e':         return 'CRAPS-ELEVEN!';
            case 'field':       return `FIELD${num ? ` · ${num}` : ''}!`;
            case 'hard':        return `HARD ${num === 4 ? 'FOUR' : num === 6 ? 'SIX' : num === 8 ? 'EIGHT' : 'TEN'}!`;
            case 'hop':         return `HOP · ${num || ''}!`;
            case 'horn':        return 'HORN!';
            case 'world':       return 'WORLD!';
            default:            return null;   // not a hero proposition
        }
    };

    // Build the flash queue: proposition hits first (ranked by size), then
    // one roll-up flash for combined line/place wins.
    const buildFlashQueue = (wins, rollTotal) => {
        const props = [];
        let lineNet = 0;
        wins.forEach((r) => {
            const net = round2(Number(r.payout || 0) - Number(r.amount || 0));
            if (net <= 0) return;
            const label = labelForBet(r);
            if (label) {
                props.push({ amount: net, key: `craps-${r.kind}-${r.number || 'x'}-${Date.now()}-${Math.random()}`, label });
            } else {
                lineNet = round2(lineNet + net);
            }
        });
        // Rank propositions by net (biggest first) so the crown jewel pops first
        props.sort((a, b) => b.amount - a.amount);
        if (lineNet > 0) {
            props.push({ amount: lineNet, key: `craps-line-${rollTotal}-${Date.now()}`, label: 'WINNER' });
        }
        return props;
    };

    const round2 = (n) => Math.round(Number(n) * 100) / 100;

    const balance = Number(user?.credits || 0) + Number(user?.bonus_credits || 0);
    const point = state?.point ?? null;

    const load = useCallback(async () => {
        try {
            const [{ data: c }, { data: s }] = await Promise.all([
                axios.get(`${API}/craps/config`, { withCredentials: true }),
                axios.get(`${API}/craps/state`, { withCredentials: true }),
            ]);
            setCfg(c);
            setState(s);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load craps table');
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    useEffect(() => { localStorage.setItem('craps-muted', muted ? '1' : '0'); }, [muted]);
    // Keep the audio hook's effects toggle in sync with the mute button
    useEffect(() => { audio.setEffects(!muted); }, [muted, audio]);
    // Map old `.mp3`-style sound names to the new procedural audio events
    const SFX_MAP = {
        'chip.mp3': 'chip',
        'deal.mp3': 'roll',
        'win.mp3': 'win_small',
        'lose.mp3': 'lose',
    };
    const playSound = (name) => {
        audio.play(SFX_MAP[name] || name);
    };

    // Group bets by kind+number for chip stacks
    const betsFor = useCallback((kind, number = null) => {
        return (state?.bets || []).filter((b) => b.kind === kind && (number == null || b.number === number));
    }, [state]);

    const placeBet = async (kind, number = null) => {
        if (!state || busy) return;
        const amt = chip;
        if (amt > balance + 0.001) { toast.error('Insufficient balance'); return; }
        if (amt < (cfg?.min_bet || 1)) { toast.error(`Min bet is $${(cfg?.min_bet || 1).toFixed(2)}`); return; }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/craps/bet`, { kind, number, amount: amt }, { withCredentials: true });
            setState(data);
            playSound('chip.mp3');
            refreshUser?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Bet rejected');
        } finally { setBusy(false); }
    };

    const undoLast = async () => {
        const bets = state?.bets || [];
        if (bets.length === 0) return;
        // Undo the newest bet that isn't already committed to a point
        const undoable = [...bets].reverse().find((b) =>
            !((b.kind === 'pass' || b.kind === 'dont_pass') && b.number != null)
            && !((b.kind === 'come' || b.kind === 'dont_come') && b.state === 'moved'));
        if (!undoable) { toast.error('Nothing to undo'); return; }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/craps/undo`, { bet_id: undoable.id }, { withCredentials: true });
            setState(data);
            refreshUser?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Undo failed');
        } finally { setBusy(false); }
    };

    // Replay every bet from the last-roll snapshot, sequentially.
    // We skip any that would no longer be legal in the current phase
    // (e.g. Pass Line if a point is on) rather than 400ing the whole batch.
    const rebet = async () => {
        if (!lastBets || lastBets.length === 0 || busy) return;
        const canBetLineNow = state?.point == null;
        const canBetComeNow = state?.point != null;
        let placed = 0, skipped = 0, insufficient = false;
        const totalCost = lastBets.reduce((s, b) => s + Number(b.amount || 0), 0);
        if (totalCost > balance + 0.001) { toast.error('Insufficient balance for full rebet'); return; }
        setBusy(true);
        try {
            for (const b of lastBets) {
                // Phase compat check — silent skip if illegal
                if ((b.kind === 'pass' || b.kind === 'dont_pass') && !canBetLineNow) { skipped++; continue; }
                if ((b.kind === 'come' || b.kind === 'dont_come') && !canBetComeNow) { skipped++; continue; }
                try {
                    const { data } = await axios.post(`${API}/craps/bet`,
                        { kind: b.kind, number: b.number, amount: b.amount },
                        { withCredentials: true });
                    setState(data);
                    placed++;
                } catch (err) {
                    if (err.response?.status === 400 && err.response?.data?.detail?.toLowerCase?.().includes('insufficient')) {
                        insufficient = true;
                        break;
                    }
                    skipped++;
                }
            }
            playSound('chip.mp3');
            if (placed > 0) {
                toast.success(`Rebet placed · ${placed} bet(s)${skipped > 0 ? ` (${skipped} skipped)` : ''}`);
            } else if (insufficient) {
                toast.error('Ran out of balance during rebet');
            } else {
                toast.message('Nothing to rebet in this phase');
            }
            refreshUser?.();
        } finally { setBusy(false); }
    };

    // Refund every takedownable bet — mirrors the /take-down backend rules
    const takeDown = async () => {
        if (busy || !state || (state.bets || []).length === 0) return;
        if (!window.confirm('Take down every removable bet and refund?')) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/craps/take-down`, {}, { withCredentials: true });
            setState(data);
            playSound('chip.mp3');
            toast.success('Bets taken down');
            refreshUser?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Take-down failed');
        } finally { setBusy(false); }
    };

    // Refund a single spot on the felt (kind + optional number). Used by the
    // "×" badge next to each chip stack so a player can pull, e.g., only their
    // Place-4 chip without touching the rest of the bets.
    const takeDownSpot = async (kind, number = null) => {
        if (busy || rolling) return;
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/craps/take-down-bet`,
                { kind, number },
                { withCredentials: true },
            );
            setState(data);
            playSound('chip.mp3');
            const label = number ? `${kind.replace('_', ' ')} ${number}` : kind.replace('_', ' ');
            toast.success(`Took down ${label}`);
            refreshUser?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Take-down failed');
        } finally { setBusy(false); }
    };

    const roll = async () => {
        if (!state || busy || rolling) return;
        if ((state.bets || []).length === 0) { toast.error('Place a bet first'); return; }
        // Snapshot the current bets — the "Rebet" button uses this to
        // replay them next round. We keep only "fresh" bets (Line without a
        // number, Come/DC still active, and all sing/one-roll bets); anything
        // already committed to a point isn't a candidate for replay.
        const snapshot = (state.bets || [])
            .filter((b) =>
                (b.kind === 'pass' && !b.number) ||
                (b.kind === 'dont_pass' && !b.number) ||
                (b.kind === 'come' && b.state === 'active') ||
                (b.kind === 'dont_come' && b.state === 'active') ||
                (b.kind === 'place') ||
                (b.kind === 'hard') ||
                (b.kind === 'field') ||
                (b.kind === 'any_7') ||
                (b.kind === 'any_craps') ||
                (b.kind === 'yo_11') ||
                (b.kind === 'c_e'))
            .map((b) => ({ kind: b.kind, number: b.number, amount: b.amount }));
        if (snapshot.length > 0) setLastBets(snapshot);
        setBusy(true);
        setRolling(true);
        setLastRoll(null);              // hide old pip faces during the tumble — final value snaps in on settle
        setResolved([]);
        playSound('deal.mp3');
        try {
            const { data } = await axios.post(`${API}/craps/roll`, {}, { withCredentials: true });
            // Let the dice animate first
            setTimeout(() => {
                setLastRoll(data.last_roll);
                setResolved(data.resolved || []);
                setState(data);
                setRolling(false);
                audio.play('roll_end');   // thud when dice settle

                // ── Slang callout — fires on EVERY roll for stickman flavor ──
                const lr = data.last_roll;
                const phase = lr.point_before == null ? 'come_out' : 'point';
                const call = SLANG(lr.d1, lr.d2, phase, lr.point_before);
                setSlangCall({ ...call, total: lr.total });
                setTimeout(() => setSlangCall(null), 1800);
                // Signature sounds for the money combos
                if (lr.total === 2) audio.play('snake_eyes');
                else if (lr.total === 11) audio.play('yo');

                // ── Celebration detection (bigger event) ──
                const passWin = (data.resolved || []).find((r) => r.kind === 'pass' && r.result === 'win');
                const sevenOut = (data.resolved || []).find((r) => r.kind === 'pass' && r.result === 'lose' && lr.total === 7);
                if (lr.point_before && lr.point_before === lr.total && passWin) {
                    // Player hit the point!
                    setCelebration({ type: 'point-hit', total: lr.total, payout: passWin.payout });
                    setTimeout(() => setCelebration(null), 3400);
                    audio.play('win_big');
                    // After a point-hit, we're back on come-out — remind them to bet the line
                    setTimeout(() => setShowPassHint(true), 3500);
                } else if (sevenOut) {
                    setCelebration({ type: 'seven-out', total: 7 });
                    setTimeout(() => setCelebration(null), 2400);
                    audio.play('seven_out');
                }

                // Standard win/loss toasts
                const wins = (data.resolved || []).filter((r) => r.result === 'win');
                const netFromRoll = wins.reduce((s, r) => s + (r.payout - r.amount), 0)
                                  - (data.resolved || []).filter((r) => r.result === 'lose').reduce((s, r) => s + r.amount, 0);
                if (wins.length > 0 && netFromRoll > 0) {
                    if (!passWin) {  // avoid double toast on point hit (celebration handles it)
                        toast.success(`🎉 +$${netFromRoll.toFixed(2)} on ${lr.total}`, { duration: 3500 });
                        playSound('win.mp3');
                    }
                    // Break out proposition wins so hero bets (Yo, Hard-8, Field,
                    // Any-7, C&E, etc.) each get their own labelled pop. Line/
                    // Place wins are grouped into a single "WINNER" flash to keep
                    // the queue short and prevent the overlay from feeling stuck.
                    const q = buildFlashQueue(wins, lr.total);
                    if (q.length > 0) {
                        // Hard cap the queue at 3 flashes so total on-screen
                        // time is bounded — safety fallback below also enforces.
                        const bounded = q.slice(0, 3);
                        playFlashAudio(bounded[0].amount);
                        setWinFlash(bounded[0]);
                        setFlashQueue(bounded.slice(1));
                        armFlashSafety();
                    }
                } else if (netFromRoll < 0 && !sevenOut) {
                    toast.message(`Rolled ${lr.total} · -$${Math.abs(netFromRoll).toFixed(2)}`);
                }
                refreshUser?.();
            }, 900);
        } catch (e) {
            setRolling(false);
            toast.error(e.response?.data?.detail || 'Roll failed');
        } finally { setBusy(false); }
    };

    // Auto-dismiss the Pass Line reminder once the player places a Pass bet (or 6 seconds pass)
    useEffect(() => {
        if (!showPassHint) return;
        const bets = state?.bets || [];
        if (bets.some((b) => b.kind === 'pass')) { setShowPassHint(false); return; }
        const t = setTimeout(() => setShowPassHint(false), 6000);
        return () => clearTimeout(t);
    }, [showPassHint, state]);

    const chipsOn = useCallback((kind, number = null) => betsFor(kind, number).map((b) => ({ id: b.id, amount: b.amount })), [betsFor]);

    // Payout hint helper — "chip $X wins $Y" for beginner enticement
    const hintFor = useCallback((kind, number = null) => {
        const c = chip;
        if (!c) return null;
        const fmt = (net) => `$${c} wins $${net.toFixed(net % 1 === 0 ? 0 : 2)}`;
        if (kind === 'place') {
            const map = { 4: [9, 5], 5: [7, 5], 6: [7, 6], 8: [7, 6], 9: [7, 5], 10: [9, 5] };
            const [n, d] = map[number] || [1, 1];
            return fmt((c * n) / d);
        }
        if (kind === 'hard') {
            const mult = (number === 6 || number === 8) ? 9 : 7;
            return fmt(c * mult);
        }
        if (kind === 'field') return `$${c} wins $${c} · 2 pays $${c * cfg.field_2_multiplier} · 12 pays $${c * cfg.field_12_multiplier}`;
        if (kind === 'any_7')     return fmt(c * 4);
        if (kind === 'any_craps') return fmt(c * 7);
        if (kind === 'yo_11')     return fmt(c * 15);
        if (kind === 'c_e')       return `$${c} · 2/3/12 wins $${c*3} · 11 wins $${c*7}`;
        if (kind === 'pass' || kind === 'dont_pass' || kind === 'come' || kind === 'dont_come') return fmt(c);
        return null;
    }, [chip, cfg]);

    // Bet kinds active in current phase — used to gray out illegal spots
    const canBetLine = point == null;    // Pass / Don't Pass — come-out only
    const canBetCome = point != null;    // Come / Don't Come — point only
    const canBetOdds = point != null;

    const totalOnFelt = useMemo(() => (state?.bets || []).reduce((s, b) => s + b.amount, 0), [state]);

    if (!cfg || !state) {
        return <div className="min-h-screen bg-[#020610] text-white flex items-center justify-center text-sm text-[#8fbea4]" data-testid="craps-loading">Loading Craps…</div>;
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#040a12] via-[#020610] to-[#010308] text-white" data-testid="craps-page">
            <WinFlash win={winFlash} label={winFlash?.label || 'WINNER'} onDone={handleFlashDone} duration={1400} />
            <header className="sticky top-0 z-30 bg-[#040a12]/85 backdrop-blur border-b border-[#0a1e26]">
                <div className="max-w-6xl mx-auto flex items-center justify-between px-3 py-3 gap-2">
                    <button onClick={() => nav('/lobby')} className="flex items-center gap-1.5 text-[#8fbea4] hover:text-white shrink-0" data-testid="craps-back-btn">
                        <ArrowLeft className="w-4 h-4" /><span className="text-sm hidden sm:inline">Casino Floor</span>
                    </button>
                    <h1 className="text-base sm:text-xl font-black tracking-wide truncate">
                        <span className="text-[#FFD700]">BETO</span>
                        <span className="text-white">·</span>
                        <span className="text-[#00E1FF]">CRAPS</span>
                    </h1>
                    <div className="flex items-center gap-2 shrink-0">
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 border border-[#FFD700]/40 backdrop-blur" data-testid="craps-wallet-chip">
                            <Coins className="w-3.5 h-3.5 text-[#FFD700]" />
                            <span className="font-mono text-sm font-black text-[#39FF14] tabular-nums" data-testid="craps-balance">${balance.toFixed(2)}</span>
                        </div>
                        <button onClick={() => setHistoryOpen(true)} className="p-1.5 rounded-md hover:bg-white/5 text-[#8fbea4]" title="History" data-testid="craps-history-btn"><History className="w-4 h-4" /></button>
                        <button onClick={() => setMuted((m) => !m)} className="p-1.5 rounded-md hover:bg-white/5" title={muted ? 'Unmute' : 'Mute'} data-testid="craps-mute-btn">
                            {muted ? <VolumeX className="w-4 h-4 text-[#8fbea4]" /> : <Volume2 className="w-4 h-4 text-[#39FF14]" />}
                        </button>
                        <button
                            onClick={() => audio.setMusic(!audio.musicOn)}
                            className="p-1.5 rounded-md hover:bg-white/5"
                            title={audio.musicOn ? 'Music: on' : 'Music: off'}
                            data-testid="craps-music-btn"
                        >
                            {audio.musicOn
                                ? <Music className="w-4 h-4 text-[#FFD700]" />
                                : <Music2 className="w-4 h-4 text-[#8fbea4]" />}
                        </button>
                        <button onClick={() => setRulesOpen(true)} className="p-1.5 rounded-md hover:bg-white/5" title="Rules" data-testid="craps-rules-btn"><HelpCircle className="w-4 h-4 text-[#FFD700]" /></button>
                    </div>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-2 md:px-4 pb-44 md:pb-6">
                <div className="relative rounded-2xl bg-gradient-to-b from-[#0a2a5c] via-[#0d3466] to-[#051a3d] border border-[#00A2FF]/30 shadow-[inset_0_0_60px_rgba(0,0,0,0.7)] mt-1.5 md:mt-3 overflow-hidden" data-testid="craps-table">
                    <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: 'radial-gradient(ellipse 70% 45% at 50% 20%, rgba(0,162,255,0.14) 0%, transparent 60%)' }} />
                    <div className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-overlay" style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0 1px, transparent 1px 3px)' }} />

                    {/* Header + puck row */}
                    <div className="relative pt-1.5 md:pt-3 pb-1 flex items-center justify-between px-2.5 md:px-6 z-10">
                        <div className="flex items-center gap-2 md:gap-3">
                            <HouseCrest size={34} accent="#00E1FF" glow="rgba(0,225,255,0.55)" />
                            <p className="font-casino text-[9px] md:text-[13px] tracking-[0.32em] md:tracking-[0.45em]" style={{ color: '#B7EBFF', textShadow: '0 0 8px rgba(183,235,255,0.6), 0 0 16px rgba(0,225,255,0.35), 0 1px 0 rgba(0,0,0,0.6)' }}>BETOHALICS · CRAPS</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <motion.div
                                key={point ?? 'off'}
                                initial={{ scale: 0.65, rotate: -30 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ type: 'spring', stiffness: 180, damping: 14 }}
                                className={`w-14 h-14 md:w-20 md:h-20 rounded-full flex items-center justify-center font-black text-2xl md:text-4xl shadow-2xl border-4 ${point ? 'bg-white text-black border-[#FFD700]' : 'bg-black/70 text-[#FF3B30] border-[#FF3B30]'}`}
                                style={point ? { boxShadow: '0 0 22px rgba(255,215,0,0.65), 0 4px 12px rgba(0,0,0,0.55)' } : {}}
                                data-testid="craps-puck">
                                {point ? point : 'OFF'}
                            </motion.div>
                            <div className="text-[10px] md:text-[13px] text-white/80 leading-tight">
                                <div className="font-black text-[#FFD700] tracking-wide">{point ? `POINT ${point}` : 'COME-OUT'}</div>
                                <div className="text-white/60">Rolls: <span className="font-mono text-white">{state.rolls}</span></div>
                            </div>
                        </div>
                    </div>

                    {/* ─── FELT-ENGRAVED PAYOUTS + HOUSE RULES ───
                        Silk-screened into the felt like the printed layout on a
                        real craps table. Amber-glow Cinzel serif reads as
                        printed wool rather than a floating card. */}
                    <div className="relative z-10 px-2.5 md:px-6 pb-1 pointer-events-none text-center" data-testid="craps-felt-rules">
                        <div className="font-casino felt-engraving text-[7px] md:text-[10px] tracking-[0.15em] md:tracking-[0.2em] leading-tight">
                            PASS · COME 1 to 1 · FIELD 1 to 1 (2 pays 2 · 12 pays 3) · PLACE 6/8 pays 7:6 · PLACE 5/9 pays 7:5 · PLACE 4/10 pays 9:5
                        </div>
                        <div className="font-casino felt-engraving text-[7px] md:text-[10px] tracking-[0.15em] md:tracking-[0.2em] leading-tight opacity-90">
                            HARD 6/8 · 9 to 1 · HARD 4/10 · 7 to 1 · ANY 7 · 4 to 1 · YO 15 to 1 · ACES / BOX 30 to 1
                        </div>
                        <div className="font-casino felt-engraving text-[7px] md:text-[10px] tracking-[0.15em] leading-tight opacity-85 mt-0.5">
                            DEALER STANDS TO SET THE POINT · WINNER PAID ON THE COME-OUT
                        </div>
                    </div>

                    {/* Dice display */}
                    <div className="relative z-10 flex items-center justify-center gap-3 py-1.5 md:py-3" data-testid="craps-dice-area">
                        <Die value={lastRoll?.d1} rolling={rolling} />
                        <Die value={lastRoll?.d2} rolling={rolling} />
                        {lastRoll && !rolling && (
                            <div className="ml-2">
                                <div className="text-[10px] md:text-[11px] text-white/70 uppercase tracking-widest">Total</div>
                                <div className="text-2xl md:text-4xl font-black text-[#00E1FF]" data-testid="craps-roll-total">{lastRoll.total}</div>
                            </div>
                        )}

                        {/* Slang callout — flashes on EVERY roll for stickman flavor. Fades fast. */}
                        <AnimatePresence>
                            {slangCall && !celebration && (
                                <motion.div
                                    key={`slang-${slangCall.name}-${state.rolls}`}
                                    initial={{ opacity: 0, y: -20, scale: 0.7 }}
                                    animate={{ opacity: 1, y: -60, scale: 1 }}
                                    exit={{ opacity: 0, y: -80, scale: 0.9 }}
                                    transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                                    className="absolute left-1/2 -translate-x-1/2 top-6 md:top-8 pointer-events-none z-30 text-center"
                                    data-testid={`craps-slang-${slangCall.total}`}
                                >
                                    <div className="font-serif font-black tracking-[0.15em] text-2xl md:text-4xl text-[#FFD700]"
                                         style={{ textShadow: '0 0 18px rgba(255,215,0,0.85), 0 2px 4px rgba(0,0,0,0.7)' }}>
                                        {slangCall.name}
                                    </div>
                                    {slangCall.sub && (
                                        <div className="text-[10px] md:text-xs uppercase tracking-widest text-white/85 mt-0.5"
                                             style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                                            {slangCall.sub}
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Celebration overlay — point hit or seven-out */}
                        <AnimatePresence>
                            {celebration && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.6, y: 20 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.9, y: -8 }}
                                    transition={{ type: 'spring', stiffness: 240, damping: 18 }}
                                    className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
                                    data-testid={`craps-celebration-${celebration.type}`}
                                >
                                    <div className={`rounded-2xl px-6 py-3 md:px-8 md:py-4 border-2 shadow-2xl backdrop-blur-sm text-center ${
                                        celebration.type === 'point-hit'
                                            ? 'bg-[#FFD700]/95 border-[#FFEB88] text-black shadow-[0_0_40px_rgba(255,215,0,0.8)]'
                                            : 'bg-[#7f1d1d]/95 border-[#FF3B30] text-white'
                                    }`}>
                                        {celebration.type === 'point-hit' ? (
                                            <>
                                                <div className="text-3xl md:text-5xl font-black tracking-wider font-serif">🎯 POINT HIT!</div>
                                                <div className="text-sm md:text-lg mt-1 font-mono">
                                                    +${celebration.payout?.toFixed(2)} on the {celebration.total}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="text-2xl md:text-4xl font-black tracking-wider font-serif">SEVEN OUT</div>
                                                <div className="text-xs md:text-sm mt-1 opacity-90">Line closes. Come-out coming up.</div>
                                            </>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Place bets row 4-5-6-8-9-10 */}
                    <div className="relative z-10 px-1.5 md:px-4 pb-1">
                        <p className="text-[9px] md:text-[11px] tracking-[0.28em] font-black text-white/70 mb-1 pl-1">PLACE BETS</p>
                        <div className="grid grid-cols-6 gap-1">
                            {[4, 5, 6, 8, 9, 10].map((n) => {
                                const payout = { 4:'9:5', 5:'7:5', 6:'7:6', 8:'7:6', 9:'7:5', 10:'9:5' }[n];
                                const isPoint = point === n;
                                const comeOn = betsFor('come').find((b) => b.number === n && b.state === 'moved');
                                const dcOn = betsFor('dont_come').find((b) => b.number === n && b.state === 'moved');
                                return (
                                    <div key={n} className="flex flex-col gap-0.5" data-testid={`place-cell-${n}`}>
                                        <BetSpot
                                            headline={`${n}`}
                                            label={`PLACE ${payout}`}
                                            hint={hintFor('place', n)}
                                            testid={`craps-place-${n}`}
                                            onClick={() => placeBet('place', n)}
                                            onTakeDown={chipsOn('place', n).length ? () => takeDownSpot('place', n) : null}
                                            chips={chipsOn('place', n)}
                                            active={isPoint}
                                            glow={isPoint}
                                            tone={isPoint ? 'gold' : 'cyan'}
                                        />
                                        {(comeOn || dcOn) && (
                                            <div className="text-[9px] text-center font-black text-[#39FF14]">
                                                {comeOn && <span>COME ${comeOn.amount}</span>}
                                                {dcOn && <span className="text-[#FF3B30] ml-1">DC ${dcOn.amount}</span>}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Center prop area — Hardways + one-roll props. Serif "printed felt" look. */}
                    <div className="relative z-10 px-1.5 md:px-4 py-1.5 mt-0.5 border-y border-white/15 bg-black/30 font-serif">
                        <p className="text-[9px] md:text-[11px] tracking-[0.28em] font-black text-white/70 mb-1 pl-1">HARDWAYS &amp; PROPS</p>
                        <div className="grid grid-cols-4 gap-1 mb-1">
                            {[4, 6, 8, 10].map((n) => {
                                const payout = (n === 4 || n === 10) ? '7:1' : '9:1';
                                const half = n / 2;
                                return (
                                    <BetSpot key={n}
                                        diceHint={<MiniDicePair a={half} b={half} size={18} />}
                                        label={`HARD ${n} ${payout}`}
                                        hint={hintFor('hard', n)}
                                        testid={`craps-hard-${n}`}
                                        onClick={() => placeBet('hard', n)}
                                        onTakeDown={chipsOn('hard', n).length ? () => takeDownSpot('hard', n) : null}
                                        chips={chipsOn('hard', n)}
                                        tone="pink"
                                        small
                                    />
                                );
                            })}
                        </div>
                        <div className="grid grid-cols-4 gap-1">
                            <BetSpot
                                diceHint={<MiniDicePair a={3} b={4} size={14} />}
                                label="ANY 7"
                                sub="4:1"
                                hint={hintFor('any_7')} testid="craps-any-7"
                                onClick={() => placeBet('any_7')}
                                onTakeDown={chipsOn('any_7').length ? () => takeDownSpot('any_7') : null}
                                chips={chipsOn('any_7')} tone="red" small />
                            <BetSpot
                                diceHint={
                                    <div className="flex items-center gap-0.5">
                                        <MiniDicePair a={1} b={1} size={11} />
                                        <MiniDicePair a={1} b={2} size={11} />
                                        <MiniDicePair a={6} b={6} size={11} />
                                    </div>
                                }
                                label="ANY CRAPS"
                                sub="7:1"
                                hint={hintFor('any_craps')} testid="craps-any-craps"
                                onClick={() => placeBet('any_craps')}
                                onTakeDown={chipsOn('any_craps').length ? () => takeDownSpot('any_craps') : null}
                                chips={chipsOn('any_craps')} tone="red" small />
                            <BetSpot
                                diceHint={<MiniDicePair a={5} b={6} size={14} />}
                                label="YO 11"
                                sub="15:1"
                                hint={hintFor('yo_11')} testid="craps-yo-11"
                                onClick={() => placeBet('yo_11')}
                                onTakeDown={chipsOn('yo_11').length ? () => takeDownSpot('yo_11') : null}
                                chips={chipsOn('yo_11')} tone="red" small />
                            <BetSpot
                                diceHint={
                                    <div className="flex items-center gap-0.5">
                                        <MiniDicePair a={1} b={2} size={11} />
                                        <MiniDicePair a={5} b={6} size={11} />
                                    </div>
                                }
                                label="C &amp; E"
                                sub="3:1 / 7:1"
                                hint={hintFor('c_e')} testid="craps-c-e"
                                onClick={() => placeBet('c_e')}
                                onTakeDown={chipsOn('c_e').length ? () => takeDownSpot('c_e') : null}
                                chips={chipsOn('c_e')} tone="red" small />
                        </div>
                    </div>

                    {/* Field bet — extra prominence: taller, glowing border, big serif label. */}
                    <div className="relative z-10 px-1.5 md:px-4 py-1.5">
                        <button
                            onClick={() => placeBet('field')}
                            className="relative w-full rounded-xl border-2 border-[#39FF14]/70 bg-gradient-to-r from-[#062f14] via-[#0a4a1e] to-[#062f14] hover:from-[#0a4a1e] hover:to-[#0a4a1e] transition py-2 md:py-3 px-2.5 md:px-5 shadow-[0_0_24px_rgba(57,255,20,0.28)]"
                            data-testid="craps-field"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-left">
                                    <div className="font-serif text-xl md:text-3xl font-black text-[#39FF14] tracking-widest leading-none" style={{ textShadow: '0 0 14px rgba(57,255,20,0.7)' }}>FIELD</div>
                                    <div className="text-[10px] md:text-xs text-white/80 mt-0.5 font-mono">
                                        Wins on <b className="text-white">2 3 4 9 10 11 12</b> · 2 pays <b className="text-[#FFD700]">{cfg.field_2_multiplier}×</b> · 12 pays <b className="text-[#FFD700]">{cfg.field_12_multiplier}×</b>
                                    </div>
                                </div>
                                {chipsOn('field').reduce((s, c) => s + c.amount, 0) > 0 && (
                                    <TableChip amount={chipsOn('field').reduce((s, c) => s + c.amount, 0)} />
                                )}
                            </div>
                        </button>
                    </div>

                    {/* Come / Don't Come — only when point set */}
                    <div className="relative z-10 px-1.5 md:px-4 py-1 grid grid-cols-2 gap-1.5">
                        <BetSpot
                            label="COME"
                            sub={canBetCome ? 'Same as Pass · 1:1' : 'After point'}
                            hint={canBetCome ? hintFor('come') : null}
                            testid="craps-come"
                            onClick={() => canBetCome && placeBet('come')}
                            chips={chipsOn('come').filter((_, i) => {
                                const bet = betsFor('come')[i];
                                return bet && bet.state === 'active';
                            })}
                            tone={canBetCome ? 'cyan' : 'gold'}
                        />
                        <BetSpot
                            label="DON'T COME"
                            sub={canBetCome ? '1:1 · 12 pushes' : 'After point'}
                            hint={canBetCome ? hintFor('dont_come') : null}
                            testid="craps-dont-come"
                            onClick={() => canBetCome && placeBet('dont_come')}
                            chips={chipsOn('dont_come').filter((_, i) => {
                                const bet = betsFor('dont_come')[i];
                                return bet && bet.state === 'active';
                            })}
                            tone={canBetCome ? 'red' : 'gold'}
                        />
                    </div>

                    {/* Pass / Don't Pass — bottom strip */}
                    <div className="relative z-10 px-1.5 md:px-4 py-1 pb-2 grid grid-cols-2 gap-1.5">
                        <div className="relative">
                            <BetSpot
                                label="PASS LINE"
                                sub={canBetLine ? '1:1' : `Point ${point} locked`}
                                hint={canBetLine ? hintFor('pass') : null}
                                testid="craps-pass"
                                onClick={() => canBetLine && placeBet('pass')}
                                chips={chipsOn('pass')}
                                tone="green"
                                active={betsFor('pass').length > 0}
                                glow={showPassHint && canBetLine}
                            />
                            {/* Pass Line reminder after a point-hit — animated finger pointer */}
                            <AnimatePresence>
                                {showPassHint && canBetLine && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -8 }}
                                        animate={{ opacity: 1, y: [0, -6, 0] }}
                                        exit={{ opacity: 0 }}
                                        transition={{ y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' }, opacity: { duration: 0.25 } }}
                                        className="absolute -top-8 md:-top-9 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-[#FFD700] text-black text-[10px] md:text-xs font-black px-2 py-0.5 rounded-full shadow-lg whitespace-nowrap pointer-events-none"
                                        data-testid="craps-pass-hint"
                                    >
                                        👇 BET THE LINE AGAIN
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        <BetSpot
                            label="DON'T PASS"
                            sub={canBetLine ? '1:1 · 12 pushes' : `Point ${point} · locked`}
                            hint={canBetLine ? hintFor('dont_pass') : null}
                            testid="craps-dont-pass"
                            onClick={() => canBetLine && placeBet('dont_pass')}
                            chips={chipsOn('dont_pass')}
                            tone="red"
                            active={betsFor('dont_pass').length > 0}
                        />
                    </div>

                    {/* Odds — appear only when point set AND matching line bet exists */}
                    {canBetOdds && (betsFor('pass').length > 0 || betsFor('dont_pass').length > 0) && (
                        <div className="relative z-10 px-2 md:px-4 pb-3 grid grid-cols-2 gap-2">
                            {betsFor('pass').length > 0 && (
                                <BetSpot label="PASS ODDS" sub={`Up to ${state.config.max_odds_per_point[point]}× on ${point}`} testid="craps-pass-odds" onClick={() => placeBet('pass_odds')}
                                    onTakeDown={chipsOn('pass_odds').length ? () => takeDownSpot('pass_odds') : null}
                                    chips={chipsOn('pass_odds')} tone="gold" small />
                            )}
                            {betsFor('dont_pass').length > 0 && (
                                <BetSpot label="DON'T PASS ODDS" sub={`Lay ${point}`} testid="craps-dont-pass-odds" onClick={() => placeBet('dont_pass_odds')}
                                    onTakeDown={chipsOn('dont_pass_odds').length ? () => takeDownSpot('dont_pass_odds') : null}
                                    chips={chipsOn('dont_pass_odds')} tone="gold" small />
                            )}
                        </div>
                    )}
                </div>

                {/* Beginner "how it works" strip — always visible, tap for full rules */}
                <button onClick={() => setRulesOpen(true)} className="mt-3 w-full text-left rounded-xl bg-[#020610]/70 border border-[#00E1FF]/30 p-3 flex items-center gap-3 hover:border-[#00E1FF] transition" data-testid="craps-howto-strip">
                    <Info className="w-5 h-5 text-[#00E1FF] shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-black text-white">
                            {point == null ? 'COME-OUT ROLL' : `POINT ${point} IS ON`}
                            <span className="text-[#8fbea4] font-normal text-xs ml-2">· Tap for full rules & payouts</span>
                        </div>
                        <div className="text-[11px] md:text-xs text-[#c9e5d4] mt-0.5 leading-snug">
                            {point == null ? (
                                <>Roll a <b className="text-[#39FF14]">7 or 11</b> to win the Pass Line 1:1 · <b className="text-[#FF3B30]">2/3/12</b> loses · <b className="text-[#FFD700]">4/5/6/8/9/10</b> sets the point.</>
                            ) : (
                                <>Roll <b className="text-[#FFD700]">{point}</b> to win Pass Line 1:1 · Roll <b className="text-[#FF3B30]">7</b> and everyone loses (except Don&apos;t Pass).</>
                            )}
                        </div>
                    </div>
                </button>

                {/* Resolved bets summary from last roll */}
                <AnimatePresence>
                    {resolved.length > 0 && !rolling && (
                        <motion.div
                            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                            className="mt-3 rounded-xl bg-[#020610]/70 border border-white/10 p-2.5 max-h-40 overflow-y-auto"
                            data-testid="craps-resolved"
                        >
                            <p className="text-[9px] tracking-widest uppercase text-[#8fbea4] font-black mb-1">Last roll settled</p>
                            <div className="space-y-0.5 text-xs">
                                {resolved.map((r, i) => (
                                    <div key={i} className="flex justify-between font-mono">
                                        <span className="text-white/80">{r.kind.replace('_', ' ')}{r.number ? ` · ${r.number}` : ''}</span>
                                        <span className={r.result === 'win' ? 'text-[#39FF14] font-bold' : r.result === 'push' ? 'text-[#FFD700]' : 'text-[#FF3B30]'}>
                                            {r.result === 'win' ? `+$${(r.payout - r.amount).toFixed(2)}` : r.result === 'push' ? 'PUSH' : `-$${r.amount.toFixed(2)}`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Action bar — Rebet + Take Down + Roll + Chip selector */}
            <div className="fixed bottom-0 left-0 right-0 md:sticky md:bottom-2 max-w-6xl md:mx-auto rounded-t-2xl md:rounded-2xl bg-[#020610]/95 backdrop-blur-md border-t border-x md:border md:border-[#0a1e26] border-[#00A2FF]/40 px-2.5 pt-1.5 pb-2 md:p-3 shadow-[0_-10px_30px_rgba(0,0,0,0.7)] z-30 pb-[env(safe-area-inset-bottom,0.5rem)] md:pb-3" data-testid="craps-action-bar">
                <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] uppercase tracking-widest text-[#8fbea4]">Chip <span className="text-white font-black">${chip}</span> · Total <span className="text-[#FFD700] font-mono font-black">${totalOnFelt.toFixed(2)}</span></p>
                    <div className="flex gap-2 items-center">
                        <button onClick={undoLast} disabled={busy || rolling || (state.bets || []).length === 0}
                                className="text-[10px] text-[#8fbea4] hover:text-white flex items-center gap-1 disabled:opacity-40" data-testid="craps-undo-btn">
                            <Undo2 className="w-3 h-3" />Undo
                        </button>
                        <button onClick={takeDown} disabled={busy || rolling || (state.bets || []).length === 0}
                                className="text-[10px] text-[#FF3B30] hover:text-white flex items-center gap-1 disabled:opacity-40" data-testid="craps-takedown-btn">
                            <Trash2 className="w-3 h-3" />Take Down
                        </button>
                    </div>
                </div>
                <div className="flex flex-nowrap justify-center gap-1.5 mb-1.5 overflow-x-auto" data-testid="craps-chip-selector">
                    {CHIP_DENOMS.map((v) => <ChipStack key={v} value={v} selected={chip === v} onClick={() => setChip(v)} />)}
                </div>
                <div className="flex justify-center gap-2">
                    <Button onClick={rebet} disabled={busy || rolling || !lastBets || lastBets.length === 0}
                            variant="outline"
                            className="border-[#FFD700]/60 text-[#FFD700] hover:bg-[#FFD700]/10 font-black px-4"
                            data-testid="craps-rebet-btn">
                        <RotateCcw className="w-4 h-4 mr-1" />Rebet
                    </Button>
                    <Button onClick={roll} disabled={busy || rolling || (state.bets || []).length === 0}
                            className="bg-[#39FF14] hover:bg-[#39FF14]/90 text-black font-black px-6 shadow-[0_0_20px_rgba(57,255,20,0.4)]"
                            data-testid="craps-roll-btn">
                        <Dice5 className="w-4 h-4 mr-1" />{rolling ? 'Rolling…' : 'ROLL DICE'}
                    </Button>
                </div>
            </div>

            <Dialog open={rulesOpen} onOpenChange={setRulesOpen}>
                <DialogContent className="bg-[#020610] border-[#0a1e26] text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="craps-rules-modal">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2"><HelpCircle className="w-5 h-5 text-[#39FF14]" />How to Play Craps</DialogTitle>
                        <DialogDescription className="text-[#8fbea4] text-xs">Come-out roll, point, and full payout tables for the BETohalics craps table.</DialogDescription>
                    </DialogHeader>
                    <CrapsRulesCard cfg={cfg} state={state} />
                </DialogContent>
            </Dialog>

            <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
                <DialogContent className="bg-[#020610] border-[#0a1e26] text-white max-w-md" data-testid="craps-history-modal">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2"><History className="w-5 h-5 text-[#39FF14]" />Recent rolls</DialogTitle>
                        <DialogDescription className="text-[#8fbea4] text-xs">Last {(state.roll_history || []).length} dice throws this session.</DialogDescription>
                    </DialogHeader>
                    <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">
                        {(state.roll_history || []).length === 0
                            ? <p className="text-[#8fbea4] text-sm py-6 text-center italic">No rolls yet — place a bet and toss!</p>
                            : [...state.roll_history].reverse().map((r, i) => (
                                <div key={i} className="flex items-center justify-between py-2 text-xs font-mono">
                                    <span className="text-white/80">🎲 {r.d1} + {r.d2} = <b className="text-[#39FF14]">{r.total}</b></span>
                                    <span className="text-[#8fbea4]">{r.point_before == null ? 'Come-out' : `Point ${r.point_before}`} → {r.point_after == null ? 'Off' : `Point ${r.point_after}`}</span>
                                </div>
                            ))
                        }
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const CrapsRulesCard = ({ cfg, state }) => {
    const oddsMax = state?.config?.max_odds_per_point || {};
    return (
        <div className="text-sm text-[#c9e5d4] space-y-3 py-1">
            <p><b className="text-white">Objective:</b> Bet on outcomes of two six-sided dice.</p>
            <div>
                <p className="font-black text-[#FFD700] mb-1">Come-out roll</p>
                <ul className="list-disc pl-5 space-y-0.5 text-xs">
                    <li><b>Pass Line wins</b> on 7 or 11 (1:1) · <b>loses</b> on 2/3/12</li>
                    <li><b>Don&apos;t Pass wins</b> on 2 or 3 (1:1) · <b>loses</b> on 7/11 · <b>pushes</b> on 12</li>
                    <li>4/5/6/8/9/10 → point established, puck flips <b>ON</b></li>
                </ul>
            </div>
            <div>
                <p className="font-black text-[#FFD700] mb-1">Point phase</p>
                <ul className="list-disc pl-5 space-y-0.5 text-xs">
                    <li>Pass wins if the <b>point</b> rolls again before a 7; loses if 7 comes first</li>
                    <li>Don&apos;t Pass is the opposite</li>
                    <li>Come / Don&apos;t Come work like Pass/Don&apos;t Pass but on any subsequent roll</li>
                </ul>
            </div>
            <div>
                <p className="font-black text-[#FFD700] mb-1">Place bets · pays</p>
                <ul className="list-disc pl-5 space-y-0.5 text-xs font-mono">
                    <li>4 &amp; 10 · <b>9:5</b></li>
                    <li>5 &amp; 9 · <b>7:5</b></li>
                    <li>6 &amp; 8 · <b>7:6</b></li>
                </ul>
            </div>
            <div>
                <p className="font-black text-[#00E1FF] mb-1">Odds (true odds, zero house edge)</p>
                <ul className="list-disc pl-5 space-y-0.5 text-xs font-mono">
                    <li>4/10 · <b>2:1</b> (up to {oddsMax[4] || 3}×)</li>
                    <li>5/9 · <b>3:2</b> (up to {oddsMax[5] || 4}×)</li>
                    <li>6/8 · <b>6:5</b> (up to {oddsMax[6] || 5}×)</li>
                </ul>
            </div>
            <div>
                <p className="font-black text-[#FF007F] mb-1">Hardways</p>
                <ul className="list-disc pl-5 space-y-0.5 text-xs font-mono">
                    <li>Hard 4 (2-2) · <b>7:1</b> · Hard 10 (5-5) · <b>7:1</b></li>
                    <li>Hard 6 (3-3) · <b>9:1</b> · Hard 8 (4-4) · <b>9:1</b></li>
                </ul>
            </div>
            <div>
                <p className="font-black text-[#39FF14] mb-1">Field &amp; one-roll props</p>
                <ul className="list-disc pl-5 space-y-0.5 text-xs font-mono">
                    <li>Field 3/4/9/10/11 · <b>1:1</b>; 2 · <b>{cfg.field_2_multiplier}:1</b>; 12 · <b>{cfg.field_12_multiplier}:1</b></li>
                    <li>Any 7 · <b>4:1</b> · Any Craps · <b>7:1</b> · Yo 11 · <b>15:1</b></li>
                    <li>C &amp; E · <b>3:1</b> on 2/3/12, <b>7:1</b> on 11</li>
                </ul>
            </div>
            <p className="text-[11px] text-[#8fbea4] italic">Server-side cryptographic dice · ${cfg.min_bet.toFixed(2)} min · ${cfg.max_bet.toFixed(2)} max per spot.</p>
        </div>
    );
};
